import { useState, useMemo, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashAlt, faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import toast from 'react-hot-toast';
import UniversalConfirmModal from '../../../components/UniversalConfirmModal/UniversalConfirmModal';
import { useNotifications } from '../../../airliner/hooks/useNotifications';
import notificationService from '../../../services/notificationService';

const Notification = () => {
  const navigate = useNavigate();
  const contentRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(1);
  const limit = 10;

  const {
    notifications,
    loading,
    handleMarkAllAsRead,
    handleDeleteNotification,
    handleDeleteAll,
    refreshNotifications,
    handleBulkAction: hookBulkAction,
    pagination
  } = useNotifications(currentPage, limit, 'all');

  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [showActionDropdown, setShowActionDropdown] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);
  const [expandedNotifications, setExpandedNotifications] = useState({});

  const Pagination = ({ currentPage, totalPages, onPageChange }) => {
    const getPageNumbers = () => {
      const pages = [];
      const maxVisiblePages = 7;

      if (totalPages <= maxVisiblePages + 4) {
        for (let i = 1; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);

        if (currentPage > 4) {
          pages.push('...');
        }

        const start = Math.max(2, currentPage - 2);
        const end = Math.min(totalPages - 1, currentPage + 2);

        for (let i = start; i <= end; i++) {
          pages.push(i);
        }

        if (currentPage < totalPages - 3) {
          pages.push('...');
        }

        pages.push(totalPages);
      }

      return pages;
    };

    return (
      <div className="flex justify-start items-center gap-1 mt-4 mb-2 px-5">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="w-8 h-8 flex items-center justify-center border border-gray-200 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
        >
          <i className="fas fa-chevron-left text-[10px]"></i>
        </button>

        {getPageNumbers().map((page, index) => (
          <button
            key={index}
            onClick={() => typeof page === 'number' && onPageChange(page)}
            disabled={page === '...'}
            className={`w-8 h-8 flex items-center justify-center border text-sm transition-all duration-200 rounded ${page === currentPage
                ? 'bg-[#ffd200] border-[#ffd200] text-white font-bold'
                : page === '...'
                  ? 'border-transparent cursor-default'
                  : 'border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
          >
            {page}
          </button>
        ))}

        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="w-8 h-8 flex items-center justify-center border border-gray-200 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
        >
          <i className="fas fa-chevron-right text-[10px]"></i>
        </button>
      </div>
    );
  };

  // Handle notification link clicks
  useEffect(() => {
    const handleLinkClick = (e) => {
      const link = e.target.closest('a[data-link]');
      if (!link) return;

      e.preventDefault();
      const dataLink = link.getAttribute('data-link');

      if (!dataLink) return;

      // Handle relative paths (e.g., /admin/users/4)
      if (dataLink.startsWith('/')) {
        navigate(dataLink);
      }
      // Handle full URLs - extract path from URL
      else if (dataLink.startsWith('http')) {
        try {
          const url = new URL(dataLink);
          navigate(url.pathname);
        } catch (error) {
          console.error('Invalid URL:', dataLink);
        }
      }
    };

    if (contentRef.current) {
      contentRef.current.addEventListener('click', handleLinkClick);
      return () => {
        contentRef.current?.removeEventListener('click', handleLinkClick);
      };
    }
  }, [navigate]);

  // Handle bulk actions (mark as read/unread)
  const handleBulkAction = async (action, ids) => {
    try {
      await hookBulkAction(action, ids);
    } catch (error) {
      console.error(`Error performing bulk action ${action}:`, error);
      throw error;
    }
  };

  // Transform notifications to match UI structure
  const messages = useMemo(() => {
    let filtered = notifications.map((notif, index) => ({
      id: notif.id || `temp-${index}`, // Fallback for null IDs
      subject: notif.title,
      content: notif.content,
      isRead: notif.read === 1,
      selected: selectedIds.includes(notif.id),
      created_at: notif.created_at,
      formattedDate: new Date(notif.created_at).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      }),
      formattedTime: new Date(notif.created_at).toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit'
      })
    }));

    if (showUnreadOnly) {
      filtered = filtered.filter(msg => !msg.isRead);
    }

    return filtered;
  }, [notifications, selectedIds, showUnreadOnly]);

  // Toggle notification expansion and mark as read
  const handleToggleExpand = async (id, isRead, originalId) => {
    // Use the display ID for state management
    setExpandedNotifications(prev => ({
      ...prev,
      [id]: !prev[id]
    }));

    // Only mark as read if we have a real ID (not a temp one) and it's unread
    if (originalId && !isRead && !expandedNotifications[id]) {
      try {
        await notificationService.markAsRead(originalId);
        refreshNotifications();
      } catch (error) {
        console.error('Failed to mark as read:', error);
      }
    }
  };
  const handleSelectAll = () => {
    const newSelectAll = !selectAll;
    setSelectAll(newSelectAll);
    if (newSelectAll) {
      setSelectedIds(messages.map(msg => msg.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectMessage = (id) => {
    setSelectedIds(prev => {
      if (prev.includes(id)) {
        return prev.filter(selectedId => selectedId !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  const handleDeleteAllClick = () => {
    setDeleteTarget('all');
    setShowDeleteModal(true);
  };

  const handleMarkAllAsReadClick = () => {
    handleMarkAllAsRead();
  };

  const handleShowOnlyUnread = () => {
    setShowUnreadOnly(!showUnreadOnly);
    setCurrentPage(1);
  };

  const handleDeleteMessageClick = (id) => {
    setDeleteTarget(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (deleteTarget === 'all') {
      await handleDeleteAll();
      setCurrentPage(1);
    } else if (deleteTarget === 'selected') {
      await handleBulkAction('delete', selectedIds);
    } else {
      await handleDeleteNotification(deleteTarget);
    }
    setDeleteTarget(null);
    setSelectedIds([]);
    setSelectAll(false);
    refreshNotifications();
  };

  const getDeleteModalContent = () => {
    if (deleteTarget === 'all') {
      return {
        title: 'Delete Message(s)?',
        message: 'You are about to delete all messages. Click "DELETE" to proceed or "CANCEL" to exit.'
      };
    } else {
      return {
        title: 'Delete Message(s)?',
        message: 'You are about to Delete one or more message(s). Click "DELETE" to proceed or "CANCEL" to exit.'
      };
    }
  };
  return (
    <div className="p-0 bg-[#F5F8FC] min-h-screen w-full relative font-['Bahnschrift_Light','Segoe_UI',Tahoma,Geneva,Verdana,sans-serif] flex flex-col">
      <div className="flex-1">
        <div className="py-5 px-[30px] mb-0 bg-transparent">
          <h1 className="m-0 text-lg text-[#333] font-bold">Messages</h1>
        </div>
        <div className="bg-white p-[15px]">
          <div className="flex justify-between items-center py-[15px] px-[30px] bg-transparent mb-[15px] lg:flex-row flex-col lg:gap-0 gap-[15px] lg:items-center items-start">
            <div className="flex items-center gap-2.5">
              <input
                type="checkbox"
                checked={selectAll}
                onChange={handleSelectAll}
                className="w-[18px] h-[18px] cursor-pointer"
              />
              <div className="relative">
                <button
                  className="text-black h-9 py-2 px-4 text-[14px] font-['Bahnschrift',sans-serif] bg-gradient-to-br from-[#ffeb00] to-[#ffd200] border-transparent rounded border cursor-pointer font-normal whitespace-nowrap hover:bg-gradient-to-br hover:from-[#ffd200] hover:to-[#ffb300]"
                  onClick={() => setShowActionDropdown(!showActionDropdown)}
                >
                  Select Action ▼
                </button>
                {showActionDropdown && (
                  <div className="absolute top-full left-0 bg-[#ffeb00] border border-[#ddd] rounded shadow-[0_4px_12px_rgba(0,0,0,0.15)] z-10 min-w-[150px] mt-[5px] overflow-hidden">

                    {/* DELETE */}
                    <div
                      onClick={() => {
                        if (selectedIds.length === 0) {
                          toast.error('Please select at least one message.');
                          setShowActionDropdown(false);
                          return;
                        }
                        setDeleteTarget('selected');
                        setShowDeleteModal(true);
                        setShowActionDropdown(false);
                      }}
                      className="py-1.5 px-[15px] cursor-pointer text-[11px] text-black font-semibold hover:bg-[#e6d300] flex items-center gap-2"
                    >
                      <span className="text-[12px]">✓</span> Delete
                    </div>

                    {/* MARK AS READ */}
                    <div
                      onClick={() => {
                        if (selectedIds.length === 0) {
                          toast.error('Please select at least one message.');
                          setShowActionDropdown(false);
                          return;
                        }

                        handleBulkAction('mark_read', selectedIds).then(() => {
                          toast.success(`${selectedIds.length} message(s) marked as read.`);
                          setSelectedIds([]);
                          setSelectAll(false);
                          refreshNotifications();
                        });

                        setShowActionDropdown(false);
                      }}
                      className="py-1.5 px-[15px] cursor-pointer text-[11px] text-black font-semibold hover:bg-[#e6d300] flex items-center gap-2"
                    >
                      <span className="text-[12px]">✓</span> Mark As Read
                    </div>

                    {/* MARK AS UNREAD */}
                    <div
                      onClick={() => {
                        if (selectedIds.length === 0) {
                          toast.error('Please select at least one message.');
                          setShowActionDropdown(false);
                          return;
                        }

                        handleBulkAction('mark_unread', selectedIds).then(() => {
                          toast.success(`${selectedIds.length} message(s) marked as unread.`);
                          setSelectedIds([]);
                          setSelectAll(false);
                          refreshNotifications();
                        });

                        setShowActionDropdown(false);
                      }}
                      className="py-1.5 px-[15px] cursor-pointer text-[11px] text-black font-semibold hover:bg-[#e6d300] flex items-center gap-2"
                    >
                      <span className="text-[12px]">✓</span> Mark As Unread
                    </div>

                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-2.5 lg:w-auto w-full lg:justify-end justify-start flex-wrap">
              <button 
                disabled={selectedIds.length > 0}
                className={`text-black h-9 py-2 px-4 text-[14px] font-['Bahnschrift',sans-serif] bg-gradient-to-br from-[#ffeb00] to-[#ffd200] border-transparent rounded border font-normal whitespace-nowrap ${
                  selectedIds.length > 0 
                    ? 'opacity-50 cursor-not-allowed' 
                    : 'cursor-pointer hover:bg-gradient-to-br hover:from-[#ffd200] hover:to-[#ffb300]'
                }`} 
                onClick={handleDeleteAllClick}
              >
                Delete All
              </button>
              <button 
                disabled={selectedIds.length > 0}
                className={`text-black h-9 py-2 px-4 text-[14px] font-['Bahnschrift',sans-serif] bg-gradient-to-br from-[#ffeb00] to-[#ffd200] border-transparent rounded border font-normal whitespace-nowrap ${
                  selectedIds.length > 0 
                    ? 'opacity-50 cursor-not-allowed' 
                    : 'cursor-pointer hover:bg-gradient-to-br hover:from-[#ffd200] hover:to-[#ffb300]'
                }`} 
                onClick={handleMarkAllAsReadClick}
              >
                Mark All As Read
              </button>
              <button 
                disabled={selectedIds.length > 0}
                className={`text-black h-9 py-2 px-4 text-[14px] font-['Bahnschrift',sans-serif] bg-gradient-to-br from-[#ffeb00] to-[#ffd200] border-transparent rounded border font-normal whitespace-nowrap ${
                  selectedIds.length > 0 
                    ? 'opacity-50 cursor-not-allowed' 
                    : 'cursor-pointer hover:bg-gradient-to-br hover:from-[#ffd200] hover:to-[#ffb300]'
                }`} 
                onClick={handleShowOnlyUnread}
              >
                {showUnreadOnly ? 'Show All' : 'Show Only Unread'}
              </button>
            </div>
          </div>

          <div className="bg-white mx-[30px] my-0 rounded border border-[#e0e0e0] shadow-[0_1px_3px_rgba(0,0,0,0.05)] md:mx-[30px] mx-[15px]" ref={contentRef}>
            {loading ? (
              <div className="text-center py-[60px] px-0 text-[#aaa] text-[14px]">Loading messages...</div>
            ) : messages.length === 0 ? (
              <div className="text-center py-[60px] px-0 text-[#aaa] text-[14px]">No messages available</div>
            ) : (
              messages.map((message) => {
                const originalId = notifications.find(n => n.title === message.subject)?.id;
                return (
                  <div
                    key={message.id}
                    className={`border-b border-[#f0f0f0] transition-colors duration-200 last:border-b-0 ${!message.isRead ? 'bg-[#e3f2fd]' : 'bg-white'}`}
                  >
                    <div className="flex items-center py-4 px-5 hover:bg-[#f9f9f9]">
                      <input
                        type="checkbox"
                        checked={message.selected}
                        onChange={() => handleSelectMessage(originalId || message.id)}
                        className="w-[18px] h-[18px] mr-[15px] cursor-pointer"
                      />
                      <div className="flex-1">
                        <button
                          onClick={() => handleToggleExpand(message.id, message.isRead, originalId)}
                          className="w-full text-left flex items-center justify-between text-[14px] text-[#333] font-bold hover:text-[#eb910c] transition-colors duration-200"
                        >
                          <span>{message.subject}</span>
                          <FontAwesomeIcon
                            icon={expandedNotifications[message.id] ? faChevronUp : faChevronDown}
                            className="text-[12px] ml-2"
                          />
                        </button>

                        {expandedNotifications[message.id] && (
                          <div className="mt-3 p-3 bg-[#f9f9f9] rounded border border-[#e0e0e0]">
                            <div className="text-[14px] text-[#666] font-semibold mb-2">
                              {message.formattedDate}, {message.formattedTime}
                            </div>
                            <div
                              className="text-[14px] text-[#333] whitespace-normal"
                              dangerouslySetInnerHTML={{ __html: message.content }}
                            />
                          </div>
                        )}
                      </div>
                      <button
                        className="bg-[#dc3545] text-white border-none rounded py-2 px-3 cursor-pointer text-[14px] transition-colors duration-200 hover:bg-[#c82333] ml-3"
                        onClick={() => handleDeleteMessageClick(originalId || message.id)}
                      >
                        <FontAwesomeIcon
                          icon={faTrashAlt}
                          style={{ color: 'white', fontSize: '16px' }}
                        />
                      </button>
                    </div>
                  </div>
                )
              })
            )}
          </div>

          <div className="m-0 whitespace-nowrap text-right flex justify-between items-center py-[15px] px-[30px] mt-[15px] md:flex-row flex-col md:gap-0 gap-[15px]">
            <div className="text-[13px] text-[#555]">
              {pagination?.total > 0
                ? `Showing ${(currentPage - 1) * limit + 1} to ${Math.min(currentPage * limit, pagination.total)} of ${pagination.total} entries`
                : 'No entries to show'}
            </div>
            {pagination?.totalPages > 1 && (
              <Pagination
                currentPage={currentPage}
                totalPages={pagination.totalPages}
                onPageChange={setCurrentPage}
              />
            )}
          </div>
        </div>
      </div>

      {/* Copyright Section */}
      <div className="pt-0 p-[15px] mr-auto ml-auto px-[15px] bg-[#f5f8fc] w-full">
        <div className="bg-white p-[15px] text-black border-t-0 text-center shadow-none pb-[10px] rounded-[5px]">
          Copyright © 2026 Jetscanner360. All rights reserved.
        </div>
      </div>

      <UniversalConfirmModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setDeleteTarget(null);
        }}
        onConfirm={confirmDelete}
        type="delete"
        title={getDeleteModalContent().title}
        message={getDeleteModalContent().message}
        theme="modern"
      />
    </div>
  );
};


export default Notification;